// User-approved long-term planning memory (Life framework / goals / projects /
// preferences). Nothing is stored automatically — items are added only via the
// user approving an AI suggestion or editing them manually in Settings. Persisted
// locally (scoped) and synced to the cloud like other user data.

import { scopedKey, getActiveUid } from "./userScope";

export const MEMORY_CATEGORIES = ["framework", "goals", "projects", "preferences"];

function key() {
  return scopedKey("planningMemory.v1");
}

function empty() {
  return { framework: [], goals: [], projects: [], preferences: [] };
}

export function getPlanningMemory() {
  try {
    const raw = localStorage.getItem(key());
    const parsed = raw ? JSON.parse(raw) : null;
    if (!parsed || typeof parsed !== "object") return empty();
    const out = empty();
    for (const c of MEMORY_CATEGORIES) {
      out[c] = (Array.isArray(parsed[c]) ? parsed[c] : []).filter(
        (i) => i && typeof i.id === "string" && typeof i.text === "string" && i.text.trim()
      );
    }
    return out;
  } catch {
    return empty();
  }
}

function readRaw() {
  try {
    const raw = localStorage.getItem(key());
    const p = raw ? JSON.parse(raw) : null;
    return p && typeof p === "object" ? p : {};
  } catch {
    return {};
  }
}

function write(mem) {
  // Preserve non-category keys (e.g. recentDecisions) that live alongside the
  // four approved-memory categories in the same synced object.
  const raw = readRaw();
  localStorage.setItem(key(), JSON.stringify({ ...raw, ...mem }));
  window.dispatchEvent(new CustomEvent("chronicles:memory-changed"));
}

// --- Recent Decision Memory (Section 5) ------------------------------------
// Short-term memory of the last 5 accepted/rejected AI suggestions. Used only to
// avoid immediately repeating a just-rejected suggestion — NEVER a permanent
// preference. Stored alongside planning memory so it syncs with existing data.
const RECENT_KEY = "recentDecisions";
export const RECENT_DECISION_LIMIT = 5;

export function getRecentDecisions() {
  const arr = readRaw()[RECENT_KEY];
  return Array.isArray(arr) ? arr.slice(0, RECENT_DECISION_LIMIT) : [];
}

export function recordDecisions(entries) {
  const clean = (entries || []).filter((e) => e && e.action && e.signature);
  if (!clean.length) return;
  const raw = readRaw();
  const prev = Array.isArray(raw[RECENT_KEY]) ? raw[RECENT_KEY] : [];
  const now = new Date().toISOString();
  const mapped = clean.map((e) => ({
    proposalId: e.proposalId || null,
    action: e.action, // "accepted" | "rejected"
    signature: String(e.signature).slice(0, 120),
    context: e.context || null,
    ts: now,
  }));
  raw[RECENT_KEY] = [...mapped, ...prev].slice(0, RECENT_DECISION_LIMIT);
  localStorage.setItem(key(), JSON.stringify(raw));
  window.dispatchEvent(new CustomEvent("chronicles:memory-changed"));
}

function newId() {
  return (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) || `m-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isEmpty(mem) {
  const m = mem || getPlanningMemory();
  return MEMORY_CATEGORIES.every((c) => !(m[c] && m[c].length));
}

export function addMemoryItem(category, text) {
  const cat = MEMORY_CATEGORIES.includes(category) ? category : "framework";
  const clean = (text || "").trim();
  if (!clean) return null;
  const now = new Date().toISOString();
  const mem = getPlanningMemory();
  const item = { id: newId(), userId: getActiveUid() || null, text: clean, createdAt: now, updatedAt: now };
  mem[cat] = [...(mem[cat] || []), item];
  write(mem);
  return item;
}

export function updateMemoryItem(category, id, text) {
  const now = new Date().toISOString();
  const mem = getPlanningMemory();
  mem[category] = (mem[category] || []).map((i) =>
    i.id === id ? { ...i, text: (text || "").trim(), updatedAt: now } : i
  );
  write(mem);
}

export function removeMemoryItem(category, id) {
  const mem = getPlanningMemory();
  mem[category] = (mem[category] || []).filter((i) => i.id !== id);
  write(mem);
}
